import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { useSharedStore } from './store/sharedStore';
import {
  Dashboard,
  Login,
  ResetPasswordRequest,
  ResetLinkSent,
  SetNewPassword,
  ResetPasswordSuccess,
  Settings,
} from './pages';
import { DashboardLayout } from './layouts/DashboardLayout';
import { UsersList, UserDetail, CreateUser } from './pages/users';
import { Plans, PlanDetail } from './pages/plans';
import { UserPlans } from './pages/userPlans/UserPlans';
import { EmployeesList } from './pages/employees';
import { LeadsList } from './pages/leads';
import { KanbanBoard } from './pages/kanban';
import { NotificationsList } from './pages/notifications';
import { AuditLogs } from './pages/auditLogs';
import { RequireAccess } from './components/RequireAccess/RequireAccess';

function App() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const checkAuth = useAuthStore(state => state.checkAuth);
  const fetchProfile = useAuthStore(state => state.fetchProfile);
  const initializeSharedData = useSharedStore(state => state.initialize);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (isAuthenticated) {
      initializeSharedData();
      void fetchProfile();
    }
  }, [isAuthenticated, initializeSharedData, fetchProfile]);

  if (!isAuthenticated) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/reset-password" element={<ResetPasswordRequest />} />
          <Route path="/reset-password/sent" element={<ResetLinkSent />} />
          <Route path="/reset-password/set" element={<SetNewPassword />} />
          <Route path="/reset-password/success" element={<ResetPasswordSuccess />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<DashboardLayout />}>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route
            path="dashboard"
            element={
              <RequireAccess routeKey="dashboard">
                <Dashboard />
              </RequireAccess>
            }
          />
          <Route
            path="kanban"
            element={
              <RequireAccess routeKey="kanban">
                <KanbanBoard />
              </RequireAccess>
            }
          />
          <Route
            path="leads"
            element={
              <RequireAccess routeKey="leads">
                <LeadsList />
              </RequireAccess>
            }
          />
          <Route
            path="users"
            element={
              <RequireAccess routeKey="users">
                <UsersList />
              </RequireAccess>
            }
          />
          <Route
            path="users/create"
            element={
              <RequireAccess routeKey="users" permission="user_mgmt:create_profile">
                <CreateUser />
              </RequireAccess>
            }
          />
          <Route
            path="users/:userId"
            element={
              <RequireAccess routeKey="users">
                <UserDetail />
              </RequireAccess>
            }
          />
          <Route
            path="plans"
            element={
              <RequireAccess routeKey="plans">
                <Plans />
              </RequireAccess>
            }
          />
          <Route
            path="plans/:planId"
            element={
              <RequireAccess routeKey="plans">
                <PlanDetail />
              </RequireAccess>
            }
          />
          <Route
            path="user-plans"
            element={
              <RequireAccess routeKey="user-plans">
                <UserPlans />
              </RequireAccess>
            }
          />
          <Route
            path="employees"
            element={
              <RequireAccess routeKey="employees">
                <EmployeesList />
              </RequireAccess>
            }
          />
          <Route
            path="settings"
            element={
              <RequireAccess routeKey="settings">
                <Settings />
              </RequireAccess>
            }
          />
          <Route
            path="notifications"
            element={
              <RequireAccess routeKey="notifications">
                <NotificationsList />
              </RequireAccess>
            }
          />
          <Route
            path="audit-logs"
            element={
              <RequireAccess routeKey="audit-logs" permission="audit_log_mgmt:read">
                <AuditLogs />
              </RequireAccess>
            }
          />
          <Route path="employees/create" element={<Navigate to="/employees" replace />} />
          <Route
            path="employees/:employeeId/edit"
            element={<Navigate to="/employees" replace />}
          />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
